/**
 * Custom React Hook for Xero authentication
 * Handles auto-connect using Custom Connections + exposes session expiry so the UI
 * can show a "connected · refreshes in Xh" status chip and a friendly reconnect prompt.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseXeroAuthResult {
  isAuthenticated: boolean | null;
  checkingAuth: boolean;
  connecting: boolean;
  connectError: string | null;
  /** ISO timestamp when the current Xero session expires (null when not connected). */
  expiresAt: string | null;
  connectToXero: () => Promise<void>;
}

interface AuthStatus {
  authenticated?: boolean;
  expiresAt?: string | null;
}

/** Re-poll the status endpoint so `expiresAt` stays fresh and a server-side expiry is noticed. */
const STATUS_POLL_MS = 60_000;

/**
 * Hook for managing Xero authentication state and auto-connection.
 */
export function useXeroAuth(): UseXeroAuthResult {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const mounted = useRef(true);

  /** Fetches /api/auth/status and syncs auth + expiry. Returns whether authenticated. */
  const refreshStatus = useCallback(async (): Promise<boolean> => {
    const response = await fetch('/api/auth/status');
    const result: { data?: AuthStatus } = await response.json();
    const authed = result.data?.authenticated === true;
    if (mounted.current) {
      setIsAuthenticated(authed);
      setExpiresAt(result.data?.expiresAt ?? null);
    }
    return authed;
  }, []);

  // Reconnect using Custom Connections (no user interaction required), then re-sync expiry.
  const connectToXero = useCallback(async () => {
    setConnecting(true);
    setConnectError(null);
    try {
      const response = await fetch('/api/auth/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to connect to Xero');
      }
      await refreshStatus(); // pick up the fresh expiresAt
    } catch (err) {
      console.error('Failed to connect to Xero:', err);
      if (mounted.current) {
        setConnectError(err instanceof Error ? err.message : 'Connection failed');
        setIsAuthenticated(false);
        setExpiresAt(null);
      }
    } finally {
      if (mounted.current) setConnecting(false);
    }
  }, [refreshStatus]);

  // Check status + auto-connect on mount, then poll to keep the expiry fresh.
  useEffect(() => {
    mounted.current = true;
    const init = async () => {
      try {
        const authed = await refreshStatus();
        setCheckingAuth(false);
        if (!authed) await connectToXero(); // auto-reconnect via Custom Connections
      } catch (err) {
        console.error('Failed to check auth status:', err);
        if (mounted.current) {
          setIsAuthenticated(false);
          setCheckingAuth(false);
          setConnectError('Failed to connect to server');
        }
      }
    };
    init();
    const poll = setInterval(() => {
      void refreshStatus().catch(() => undefined);
    }, STATUS_POLL_MS);
    return () => {
      mounted.current = false;
      clearInterval(poll);
    };
  }, [refreshStatus, connectToXero]);

  return {
    isAuthenticated,
    checkingAuth,
    connecting,
    connectError,
    expiresAt,
    connectToXero,
  };
}
